/**
 * **One kind's level** — the figure a low-sleep or low-energy reading is judged against
 * (attention-queue R25), as a stepper that stands on the row at all times
 * (`docs/design/zadania-progi-2026-10-02/`, variant A, the owner's choice).
 *
 * ── Every step writes at once, like the switch beside it ────────────────────────────────────
 * There is no „Zapisz" on this screen. A press on − or +, an arrow key, or a typed value that is
 * one of the server's levels goes out as its own PUT, optimistically (`lib/useTaskKinds`).
 *
 * A HELD arrow is dropped (`event.repeat`): each step is a write, and a key held for a second would
 * be thirty of them.
 *
 * ── The figure is a real spinbutton ─────────────────────────────────────────────────────────
 * A text field with the spinbutton role, so the value, its range and its unit reach assistive
 * technology as a value: arrows step, Home and End go to the ends of the range, Enter commits what
 * was typed and Escape gives the level in force back. The two ends stay FOCUSABLE at the range's
 * edge and when the kind is off — `aria-disabled`, never `disabled` — because a control that drops
 * out of the tab order hides the very reason it will not move; the reason is its description.
 *
 * The group carries the kind's name once; the field inside it is „Próg", so the name is not read
 * twice on the way in.
 *
 * ── The field's content stays selected while it has focus ───────────────────────────────────
 * It prints the level WITH its unit, so a caret left after „7,0 h" turns the next keystroke into
 * „7,0 h7". After a step, a commit or Escape the new figure is selected again, and typing replaces
 * it. „Przywróć …" unmounts under its own press, so it hands focus to the field.
 *
 * ── A typed value the server would refuse is refused HERE ───────────────────────────────────
 * Outside the range or between two steps (`model/taskKinds`' `typedLevel`): the field keeps what was
 * typed, the groove takes the error ring, and the row says which values are accepted — the range
 * and the step, both the catalogue's. The sentence is the field's description. Nothing is sent.
 *
 * ── A muted kind keeps its level ────────────────────────────────────────────────────────────
 * The level is stored whether or not the kind is on (R25), so it is drawn and says it is not in
 * force, and the row does not change height when the switch is pressed.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { VitalSpelling } from '~/features/attention-queue';
import { numberFormat } from '~/shared/lib/intlCache';
import stepper from '~/shared/ui/stepper.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { steppedLevel, typedLevel, type KindLevel as Level } from '../model/taskKinds';
import { RestoreIcon } from './TaskIcons';
import styles from './taskKinds.module.css';

interface KindLevelProps {
  /** The kind's own name, for the group's accessible name. */
  readonly name: string;
  readonly level: Level;
  /** The kind is off: the level is drawn and cannot be moved. */
  readonly muted: boolean;
  /** How this reading's figures are spelled — the same words every card prints a level in. */
  readonly spelling: VitalSpelling;
  readonly onChange: (value: number) => void;
  readonly onReset: () => void;
}

export function KindLevel({ name, level, muted, spelling, onChange, onReset }: KindLevelProps) {
  const { t, i18n } = useTranslation();
  const field = useRef<HTMLInputElement>(null);
  const noteId = useId();
  const errorId = useId();
  const endId = useId();
  /** What the trainer is typing, or `null` while the field shows the level in force. */
  const [draft, setDraft] = useState<string | null>(null);
  /** What was typed is not a level the server offers. */
  const [invalid, setInvalid] = useState(false);
  const below = steppedLevel(level, -1);
  const above = steppedLevel(level, 1);
  const changed = level.value !== level.fallback;
  const spell = spelling.level;
  const shown = draft ?? spell(level.value);
  /** A bound of the range or its step, bare: "4", "9", "0,5". */
  const bare = (value: number) => numberFormat(i18n.language, { maximumFractionDigits: 1 }).format(value);

  // The figure in force changed under a focused field — a step, a commit, Escape, a reset: select
  // it, so the next keystroke replaces it. Never while the trainer is typing.
  useEffect(() => {
    if (draft === null && document.activeElement === field.current) field.current?.select();
  }, [draft, shown]);

  /** Give the field back to the level in force, whatever was typed. */
  const settle = () => {
    setDraft(null);
    setInvalid(false);
  };

  const move = (next: number | null) => {
    if (muted || next === null) return;
    settle();
    onChange(next);
  };

  const commit = () => {
    if (draft === null) return;
    const typed = typedLevel(draft, level);
    if (typed === null) {
      setInvalid(true);
      return;
    }
    settle();
    if (typed !== level.value) onChange(typed);
  };

  /** Why an end will not move: the kind is off, or the level stands at that end of the range. */
  const endReason = (next: number | null) => (muted ? noteId : next === null ? endId : undefined);

  return (
    <>
      <div className={styles.levelLine}>
        <span
          className={`${String(stepper.stepper)} ${String(styles.level)} ${invalid ? String(styles.levelInvalid) : ''}`}
          role="group"
          aria-label={t('web:tasks.kinds.level.group', { name })}
        >
          <button
            type="button"
            className={stepper.button}
            aria-label={t('web:tasks.kinds.level.lower')}
            aria-disabled={muted || below === null}
            aria-describedby={endReason(below)}
            onClick={() => {
              move(below);
            }}
          >
            <span aria-hidden="true">−</span>
          </button>
          <input
            ref={field}
            className={`${String(stepper.value)} ${String(styles.levelValue)}`}
            type="text"
            inputMode="decimal"
            role="spinbutton"
            aria-label={t('web:tasks.kinds.level.field')}
            aria-valuenow={level.value}
            aria-valuemin={level.min}
            aria-valuemax={level.max}
            aria-valuetext={spell(level.value)}
            aria-invalid={invalid ? true : undefined}
            aria-disabled={muted ? true : undefined}
            aria-describedby={invalid ? errorId : muted ? noteId : undefined}
            readOnly={muted}
            value={shown}
            onChange={(event) => {
              setDraft(event.target.value);
              setInvalid(false);
            }}
            onFocus={(event) => {
              event.target.select();
            }}
            onBlur={commit}
            onKeyDown={(event) => {
              switch (event.key) {
                case 'ArrowUp':
                  event.preventDefault();
                  if (!event.repeat) move(above);
                  break;
                case 'ArrowDown':
                  event.preventDefault();
                  if (!event.repeat) move(below);
                  break;
                case 'Home':
                  event.preventDefault();
                  if (!event.repeat) move(level.value === level.min ? null : level.min);
                  break;
                case 'End':
                  event.preventDefault();
                  if (!event.repeat) move(level.value === level.max ? null : level.max);
                  break;
                case 'Enter':
                  commit();
                  break;
                case 'Escape':
                  settle();
                  break;
              }
            }}
          />
          <button
            type="button"
            className={stepper.button}
            aria-label={t('web:tasks.kinds.level.higher')}
            aria-disabled={muted || above === null}
            aria-describedby={endReason(above)}
            onClick={() => {
              move(above);
            }}
          >
            <span aria-hidden="true">+</span>
          </button>
        </span>
        {muted ? (
          <span className={styles.levelNote} id={noteId}>
            {t('web:tasks.kinds.level.notInForce')}
          </span>
        ) : invalid ? null : changed ? (
          <button
            type="button"
            className={styles.levelReset}
            aria-label={t('web:tasks.kinds.level.restoreLabel', { value: spell(level.fallback) })}
            onClick={() => {
              settle();
              onReset();
              // This button is gone once the level is the default again: the keyboard goes to the
              // field the reset was about instead of falling to `<body>`.
              field.current?.focus();
            }}
          >
            <RestoreIcon className={styles.levelResetGlyph} />
            {t('web:tasks.kinds.level.restore', { value: spell(level.fallback) })}
          </button>
        ) : (
          <span className={styles.levelNote}>{t('web:tasks.kinds.level.isDefault')}</span>
        )}
        {!muted && (below === null || above === null) ? (
          <span className={hidden.srOnly} id={endId}>
            {t(below === null ? 'web:tasks.kinds.level.atLowest' : 'web:tasks.kinds.level.atHighest')}
          </span>
        ) : null}
      </div>
      {invalid ? (
        <p className={styles.rowError} id={errorId} role="alert">
          {/* A whole-number scale is asked for as one; any other step is named. */}
          {level.step === 1
            ? t('web:tasks.kinds.level.invalidWhole', {
                min: bare(level.min),
                max: spelling.withUnit(bare(level.max)),
              })
            : t('web:tasks.kinds.level.invalidStepped', {
                min: bare(level.min),
                max: spelling.withUnit(bare(level.max)),
                step: bare(level.step),
              })}
        </p>
      ) : null}
    </>
  );
}
